import { validatePairingQuality } from "./pairingQuality.js";
import { PLACEHOLDER_PATTERN } from "./recipeEnhancer.js";

const DEFAULT_TARGET_MENU_COUNT = 500;
const DEFAULT_MIN_MENU_COUNT = 250;

const FIELD_LABELS = {
  tr: {
    anaYemek: "ana yemek",
    corba: "çorba",
    salata: "salata / eşlikçi",
  },
  en: {
    anaYemek: "main dish",
    corba: "starter / soup",
    salata: "side / salad",
  },
};

const getLocaleLabels = (locale) => FIELD_LABELS[locale] || FIELD_LABELS.tr;

const normalizeText = (value) => String(value || "").trim();

const normalizeDishKey = (value) =>
  normalizeText(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ");

const getMenuSignature = (menu) =>
  [menu?.anaYemek, menu?.corba, menu?.salata].map(normalizeDishKey).join("||");

export const COOKING_DATA_TARGETS = {
  minMenuCount: DEFAULT_MIN_MENU_COUNT,
  targetMenuCount: DEFAULT_TARGET_MENU_COUNT,
  supportedLocales: ["tr", "en"],
};

export function buildCookingDataset({
  locale = "tr",
  menus = [],
  recipes = {},
  minMenuCount = DEFAULT_MIN_MENU_COUNT,
  targetMenuCount = DEFAULT_TARGET_MENU_COUNT,
} = {}) {
  const labels = getLocaleLabels(locale);
  const normalizedMenus = menus.map((menu) => ({
    ...menu,
    locale,
    recipeRefs: {
      main: menu.anaYemek,
      starter: menu.corba,
      side: menu.salata,
    },
    displaySlots: [
      { key: "main", legacyKey: "anaYemek", label: labels.anaYemek, value: menu.anaYemek },
      { key: "starter", legacyKey: "corba", label: labels.corba, value: menu.corba },
      { key: "side", legacyKey: "salata", label: labels.salata, value: menu.salata },
    ],
  }));

  return {
    locale,
    menus: normalizedMenus,
    recipes,
    stats: getCookingDataStats(normalizedMenus, recipes),
    targets: {
      minMenuCount,
      targetMenuCount,
    },
  };
}

export function getRecipeFromCookingData(dataset, dishName) {
  if (!dataset || !dishName) return null;
  return dataset.recipes?.[dishName] || null;
}

export function getCookingDataStats(menus = [], recipes = {}) {
  const uniqueMenuSignatures = new Set();
  const duplicateMenuSignatures = new Map();
  const dishNames = new Set();
  const recipeBodies = new Set();

  menus.forEach((menu) => {
    const signature = getMenuSignature(menu);
    if (uniqueMenuSignatures.has(signature)) {
      duplicateMenuSignatures.set(signature, (duplicateMenuSignatures.get(signature) || 1) + 1);
    }
    uniqueMenuSignatures.add(signature);
    [menu.anaYemek, menu.corba, menu.salata].forEach((name) => {
      const normalizedName = normalizeText(name);
      if (normalizedName) dishNames.add(normalizedName);
    });
  });

  Object.values(recipes || {}).forEach((recipe) => {
    if (!recipe) return;
    recipeBodies.add(JSON.stringify({
      kategori: recipe.kategori,
      sure: recipe.sure,
      porsiyon: recipe.porsiyon,
      malzemeler: recipe.malzemeler,
      yapilis: recipe.yapilis,
    }));
  });

  return {
    menuCount: menus.length,
    uniqueMenuCount: uniqueMenuSignatures.size,
    duplicateMenuCount: menus.length - uniqueMenuSignatures.size,
    duplicateMenuGroups: duplicateMenuSignatures.size,
    uniqueDishCount: dishNames.size,
    recipeCount: Object.keys(recipes || {}).length,
    uniqueRecipeBodyCount: recipeBodies.size,
  };
}

export function validateCookingDataset(dataset, options = {}) {
  const {
    strict = false,
    requireTargetCount = false,
    requireCuratedMetadata = false,
    requireDetailedRecipes = false,
    minMenuCount = dataset?.targets?.minMenuCount ?? DEFAULT_MIN_MENU_COUNT,
    targetMenuCount = dataset?.targets?.targetMenuCount ?? DEFAULT_TARGET_MENU_COUNT,
  } = options;
  const errors = [];
  const warnings = [];
  const menus = dataset?.menus || [];
  const recipes = dataset?.recipes || {};
  const seenIds = new Set();
  const seenSignatures = new Map();

  if (!dataset?.locale) {
    errors.push("Dataset locale bilgisi eksik.");
  }

  if (menus.length < minMenuCount) {
    errors.push(`${dataset?.locale || "?"}: En az ${minMenuCount} menü bekleniyor, mevcut ${menus.length}.`);
  }

  if (menus.length < targetMenuCount) {
    warnings.push(`${dataset?.locale || "?"}: Hedef ${targetMenuCount} menü, mevcut ${menus.length}. Sonraki veri genişletme aşamasında tamamlanmalı.`);
  }

  if (requireTargetCount && menus.length !== targetMenuCount) {
    errors.push(`${dataset?.locale || "?"}: Menü sayısı tam olarak ${targetMenuCount} olmalı, mevcut ${menus.length}.`);
  }

  menus.forEach((menu, index) => {
    if (!Number.isInteger(menu.id)) {
      errors.push(`${dataset.locale}: ${index + 1}. menünün id değeri geçersiz.`);
    } else if (seenIds.has(menu.id)) {
      errors.push(`${dataset.locale}: Tekrarlanan menü id değeri: ${menu.id}.`);
    }
    seenIds.add(menu.id);

    ["anaYemek", "corba", "salata"].forEach((field) => {
      if (!normalizeText(menu[field])) {
        errors.push(`${dataset.locale}: ${menu.id || index + 1} id'li menüde ${field} alanı boş.`);
      }
    });

    const signature = getMenuSignature(menu);
    if (seenSignatures.has(signature)) {
      errors.push(`${dataset.locale}: ${seenSignatures.get(signature)} ve ${menu.id} menüleri aynı kombinasyona sahip.`);
    } else {
      seenSignatures.set(signature, menu.id);
    }

    if (menu.culture && menu.culture !== dataset.locale) {
      errors.push(`${dataset.locale}: ${menu.id} id'li menünün kültür etiketi "${menu.culture}" olarak hatalı ayarlanmış.`);
    }

    if (requireCuratedMetadata && (!menu.pairingProfile || !Number.isInteger(menu.variant))) {
      errors.push(`${dataset.locale}: ${menu.id} id'li menüde eşleşme profili bilgisi eksik.`);
    }

    [menu.anaYemek, menu.corba, menu.salata].forEach((dishName) => {
      if (normalizeText(dishName) && !recipes[dishName]) {
        errors.push(`${dataset.locale}: "${dishName}" için tarif bulunamadı.`);
      }
    });
  });

  Object.entries(recipes).forEach(([name, recipe]) => {
    if (!normalizeText(name)) {
      errors.push(`${dataset.locale}: İsimsiz tarif anahtarı bulundu.`);
    }
    if (!recipe || typeof recipe !== "object") {
      errors.push(`${dataset.locale}: "${name}" tarifi geçersiz.`);
      return;
    }
    if (!Array.isArray(recipe.malzemeler) || recipe.malzemeler.length === 0) {
      errors.push(`${dataset.locale}: "${name}" tarifinde malzeme listesi eksik.`);
    }
    if (!Array.isArray(recipe.yapilis) || recipe.yapilis.length === 0) {
      errors.push(`${dataset.locale}: "${name}" tarifinde yapılış adımları eksik.`);
    }
    if (strict && Array.isArray(recipe.yapilis) && recipe.yapilis.length < 5) {
      warnings.push(`${dataset.locale}: "${name}" tarifi premium hedef için daha detaylandırılmalı.`);
    }

    if (requireDetailedRecipes) {
      if (recipe.recipeVersion !== 2) {
        errors.push(`${dataset.locale}: "${name}" tarifi güncel ayrıntılı tarif sürümünde değil.`);
      }
      if (!Array.isArray(recipe.malzemeler) || recipe.malzemeler.length < 6) {
        errors.push(`${dataset.locale}: "${name}" tarifinde en az 6 ölçülü malzeme bekleniyor.`);
      }
      if (!Array.isArray(recipe.yapilis) || recipe.yapilis.length < 6) {
        errors.push(`${dataset.locale}: "${name}" tarifinde en az 6 açıklayıcı adım bekleniyor.`);
      }
      ["prepTime", "cookTime", "totalTime", "difficultyKey", "storage", "safetyNote"].forEach((field) => {
        if (!normalizeText(recipe[field])) {
          errors.push(`${dataset.locale}: "${name}" tarifinde ${field} alanı eksik.`);
        }
      });
      if (!Array.isArray(recipe.tips) || recipe.tips.length < 2) {
        errors.push(`${dataset.locale}: "${name}" tarifinde püf noktaları eksik.`);
      }
      if (!Array.isArray(recipe.substitutions) || recipe.substitutions.length < 2) {
        errors.push(`${dataset.locale}: "${name}" tarifinde alternatif malzeme bilgisi eksik.`);
      }
      if (!Array.isArray(recipe.allergens)) {
        errors.push(`${dataset.locale}: "${name}" tarifinde alerjen listesi eksik.`);
      }
      if (PLACEHOLDER_PATTERN.test(JSON.stringify(recipe))) {
        errors.push(`${dataset.locale}: "${name}" tarifinde genel şablon/yer tutucu ifade kaldı.`);
      }
    }
  });

  validatePairingQuality(menus, dataset?.locale).forEach((qualityError) => {
    errors.push(`${dataset?.locale || "?"}: ${qualityError}`);
  });

  return {
    ok: errors.length === 0,
    strict,
    errors,
    warnings,
    stats: dataset?.stats || getCookingDataStats(menus, recipes),
  };
}
